/**
 * The case workbench: one case — a report — with its header and its views:
 * the Brief (the first view: the case for someone picking it up cold, the
 * checklist, the open asks, Start / Continue / Release) · Needs you
 * (everything that needs a person, worked top to bottom) · Evidence ·
 * Document · Files · Activity — cross-linked through the URL. Setup, for a
 * case set up from a request, is its own view until the request starts.
 *
 * Shown only for a case the API says the caller may open (live —
 * `CaseDetail.workbench`). One poll keeps it current: the header, on its
 * ETag; the views read again only when the header's version — the case's
 * change token — moves.
 *
 * One right column — docked beside the views on a wide screen, a drawer
 * otherwise — holds the case's Assistant (opened from the header, kept per
 * browser) or a record. A record named anywhere (an agent's words, a draft's
 * citations, the Assistant's links) opens there, by its number on the case
 * (the record index), and the view it was opened from keeps its place;
 * closing it brings the Assistant back if it was open. A passage selected in
 * the Document view ("Ask the Assistant") and the item in focus go with a
 * message; the Assistant's links open the views.
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Badge, Box, Card, Center, Drawer, Loader, Stack, Tabs, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import type { PendingEdits } from '@/components/Containers/DocxContent';
import { CopilotColumn, type CaseLink } from '@/components/Copilot';
import {
  useWorkbenchActivity,
  useWorkbenchBrief,
  useWorkbenchChecklist,
  useWorkbenchConversations,
  useWorkbenchDocuments,
  useWorkbenchEvidence,
  useWorkbenchEvidenceIndex,
  useWorkbenchEvidenceItem,
  useWorkbenchFiles,
  useWorkbenchHeader,
  useWorkbenchInterventions,
  useWorkbenchPrefills,
  useWorkbenchReviewIssues,
  useWorkbenchSetup,
} from '@/hooks/useWorkbench';
import type { CopilotFocus, CopilotPassage } from '@/types/copilot';
import {
  WORKBENCH_EVIDENCE_MAX_PAGE,
  type EvidenceQuery,
  type WorkbenchHeader as Header,
  type Intervention,
  type ReviewIssue,
  type WorkbenchView,
} from '@/types/workbench';
import { ActivityView } from './ActivityView';
import { CaseBrief } from './CaseBrief';
import { ConversationsView } from './ConversationsView';
import { DocumentView } from './DocumentView';
import type { DraftChanges } from './DraftView';
import {
  evidenceQuery,
  EvidenceView,
  firstFilters,
  placeOf,
  type EvidenceFilters,
} from './EvidenceView';
import { FilesView, openWorkbenchFile } from './FilesView';
import { useWorkbenchLinks, WorkbenchLinks } from './links';
import { NeedsYou } from './NeedsYou';
import { needsYouMemory, type NeedsYouItem, type NeedsYouMemory } from './needsYouOrder';
import { RecordColumn } from './RecordColumn';
import { RecordIndexProvider } from './recordIndex';
import { SetupView } from './setup/SetupView';
import { WorkbenchHeader } from './WorkbenchHeader';
import classes from './Workbench.module.css';

/** Wide enough for the right column — the Assistant or a record — beside
 * the workbench. */
const DOCKED = '(min-width: 80em)';
const COPILOT_OPEN = 'workbench.copilot.open';

/** One empty list, so "nothing open yet" is the same prop every render. */
const NO_INTERVENTIONS: Intervention[] = [];

/** The records the Document view looks through for the paragraphs they were
 * found in: the first page the API serves. */
const DOCUMENT_EVIDENCE: EvidenceQuery = { page: 1, pageSize: WORKBENCH_EVIDENCE_MAX_PAGE };

function storedOpen(): boolean {
  try {
    return globalThis.localStorage?.getItem(COPILOT_OPEN) === '1';
  } catch {
    return false;
  }
}

/** The item in focus in the workbench, as the Assistant is offered it. */
function focusOf(links: WorkbenchLinks): CopilotFocus | null {
  if (links.checklist) {
    return { type: 'checklist', id: links.checklist, label: null };
  }
  if (links.evidence) {
    return { type: 'evidence', id: links.evidence, label: null };
  }
  if (links.intervention) {
    return { type: 'ask', id: links.intervention, label: null };
  }
  if (links.issue) {
    return { type: 'review_issue', id: links.issue, label: null };
  }
  if (links.file) {
    return { type: 'file', id: links.file, label: null };
  }
  if (links.para) {
    return { type: 'para', id: links.para, label: null };
  }
  return null;
}

interface WorkbenchProps {
  reportId: string;
  familyKey: string;
  /** The case left the workbench (handed back): the case page refetches. */
  onHandedBack: () => void | Promise<void>;
  /** An evaluation copy was removed: the page leaves it. */
  onRemoved?: () => void | Promise<void>;
  /** The view it opens on when the URL names none; else Setup until the
   * request starts, then the Brief. */
  initialView?: WorkbenchView;
}

function TabLabel({ label, count }: Readonly<{ label: string; count?: number }>) {
  return (
    <>
      {label}
      {count !== undefined && count > 0 && (
        <Badge size="xs" variant="light" color="gray" ml={6}>
          {count}
        </Badge>
      )}
    </>
  );
}

/**
 * The page re-renders when the header's version moves, a link changes or the
 * Assistant opens; every part below is memoised and handed stable callbacks,
 * so each re-renders only when what it shows does.
 */
export function Workbench({
  reportId,
  familyKey,
  onHandedBack,
  onRemoved,
  initialView,
}: Readonly<WorkbenchProps>) {
  const header = useWorkbenchHeader(reportId);
  const version = header.data?.version ?? null;
  // A case set up from a request has Setup; one with no request has none.
  const setup = header.data?.setup ?? null;
  const opening = initialView === 'setup' && !setup ? undefined : initialView;
  const links = useWorkbenchLinks(opening ?? (setup && !setup.started ? 'setup' : 'brief'));
  // A link to Setup on a case with none opens the Brief.
  const view = links.view === 'setup' && !setup ? 'brief' : links.view;
  const interventions = useWorkbenchInterventions(reportId, version);
  const open = interventions.data ?? NO_INTERVENTIONS;
  const brief = useWorkbenchBrief(reportId, version);
  const index = useWorkbenchEvidenceIndex(reportId, version);
  // Text edits not saved yet — the report's, and a draft's with its layout
  // fixes: kept here so they survive a switch of view.
  const [edits, setEdits] = useState<PendingEdits>(() => new Map());
  const [draftChanges, setDraftChanges] = useState<DraftChanges | null>(null);
  const [copilotOpen, setCopilotOpen] = useState(storedOpen);
  const [passage, setPassage] = useState<CopilotPassage | null>(null);
  // What Needs you remembers across views (the page is keyed by the case).
  const [needsYou] = useState(needsYouMemory);
  const closedHere = useCallback(
    (ids: string[]) => {
      for (const id of ids) {
        needsYou.answered.add(id);
      }
    },
    [needsYou]
  );
  const docked = useMediaQuery(DOCKED) === true;
  const unsaved =
    edits.size + (draftChanges ? draftChanges.edits.size + draftChanges.fixes.size : 0);

  useEffect(() => {
    if (unsaved === 0) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  const refetchHeader = header.refetch;
  const refetchInterventions = interventions.refetch;
  const refetchBrief = brief.refetch;
  const refresh = useCallback(async () => {
    await Promise.all([refetchHeader(), refetchInterventions(), refetchBrief()]);
  }, [refetchHeader, refetchInterventions, refetchBrief]);

  const openCopilot = useCallback((next: boolean) => {
    setCopilotOpen(next);
    try {
      globalThis.localStorage?.setItem(COPILOT_OPEN, next ? '1' : '0');
    } catch {
      // Private mode: it just isn't remembered.
    }
  }, []);
  const closeCopilot = useCallback(() => openCopilot(false), [openCopilot]);
  const toggleCopilot = useCallback(() => openCopilot(!copilotOpen), [openCopilot, copilotOpen]);

  const askCopilot = useCallback(
    (selected: CopilotPassage) => {
      setPassage(selected);
      openCopilot(true);
    },
    [openCopilot]
  );

  const { openRecord, focus } = links;
  const closeRecord = useCallback(() => openRecord(null), [openRecord]);
  const showInEvidence = useCallback(
    (id: string) => focus({ view: 'evidence', evidence: id, record: null }),
    [focus]
  );
  const openChecklistItem = useCallback(
    (id: string) => focus({ view: 'brief', checklist: id }),
    [focus]
  );
  const openRecordsOf = useCallback(
    (id: string) => focus({ view: 'evidence', checklist: id }),
    [focus]
  );
  const openAsk = useCallback(
    (id: string) => focus({ view: 'needs_you', intervention: id }),
    [focus]
  );
  const openDraft = useCallback((id: string) => focus({ view: 'document', doc: id }), [focus]);
  const openSetup = useMemo(
    () => (setup ? () => focus({ view: 'setup' }) : undefined),
    [setup, focus]
  );
  const openFile = useCallback((key: string) => void openWorkbenchFile(reportId, key), [reportId]);

  /** An Assistant link opens its view, the item in focus; a record, beside it. */
  const openLink = useCallback(
    (link: CaseLink) => {
      switch (link.scheme) {
        case 'evidence':
          openRecord(link.id);
          break;
        case 'checklist':
          focus({ view: 'brief', checklist: link.id });
          break;
        case 'ask':
          focus({ view: 'needs_you', intervention: link.id });
          break;
        case 'task':
          focus({ view: 'activity' });
          break;
        case 'file':
          focus({ view: 'files', file: link.id });
          break;
        case 'para':
          focus({ view: 'document', para: link.id, doc: link.documentId });
          break;
      }
    },
    [focus, openRecord]
  );

  const inFocus = useMemo(() => focusOf(links), [links]);

  if (!header.data) {
    return header.error ? (
      <Card withBorder padding="lg" radius="md">
        <Text size="sm" c="red">
          The workbench couldn&apos;t be loaded: {header.error.message}
        </Text>
      </Card>
    ) : (
      <Center h={200}>
        <Loader />
      </Center>
    );
  }

  // The right column: the record open beside the view, else the Assistant.
  const column = links.record ? (
    <RecordColumn
      header={header.data}
      recordId={links.record}
      docked={docked}
      onClose={closeRecord}
      onShowInEvidence={showInEvidence}
      onOpenChecklistItem={openChecklistItem}
    />
  ) : copilotOpen ? (
    <CopilotColumn
      reportId={reportId}
      version={version}
      passage={passage}
      onPassageChange={setPassage}
      focus={inFocus}
      onOpenLink={openLink}
      onChanged={refresh}
      onClose={closeCopilot}
      docked={docked}
    />
  ) : null;

  const workbench = (
    <Stack gap="md" data-testid="workbench">
      <WorkbenchHeader
        header={header.data}
        onHandedBack={onHandedBack}
        onRemoved={onRemoved}
        copilotOpen={copilotOpen}
        onToggleCopilot={toggleCopilot}
      />
      <Tabs
        value={view}
        onChange={(v) => v && focus({ view: v as WorkbenchView })}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="brief" data-testid="tab-brief">
            Brief
          </Tabs.Tab>
          {setup && (
            <Tabs.Tab value="setup" data-testid="tab-setup">
              Setup
            </Tabs.Tab>
          )}
          <Tabs.Tab value="needs_you" data-testid="tab-needs_you">
            Needs you
            {header.data.needsYou.total > 0 && (
              <Badge size="xs" variant="light" color="orange" ml={6}>
                {header.data.needsYou.total}
              </Badge>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="evidence" data-testid="tab-evidence">
            <TabLabel label="Evidence" count={header.data.counts.evidence} />
          </Tabs.Tab>
          <Tabs.Tab value="document" data-testid="tab-document">
            <TabLabel label="Document" count={unsaved} />
          </Tabs.Tab>
          <Tabs.Tab value="files" data-testid="tab-files">
            <TabLabel label="Files" count={header.data.counts.files} />
          </Tabs.Tab>
          {!header.data.evaluationOf && (
            <Tabs.Tab value="chat" data-testid="tab-chat">
              Chat
              {header.data.counts.conversationsAwaiting > 0 && (
                <Badge size="xs" variant="light" color="orange" ml={6}>
                  {header.data.counts.conversationsAwaiting}
                </Badge>
              )}
            </Tabs.Tab>
          )}
          <Tabs.Tab value="activity" data-testid="tab-activity">
            Activity
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="brief" pt="md">
          <CaseBrief
            header={header.data}
            brief={brief.data}
            onChanged={refresh}
            onOpenAsk={openAsk}
            onOpenLink={openLink}
            onOpenRecords={openRecordsOf}
            onOpenDraft={openDraft}
            onOpenSetup={openSetup}
            focusedChecklistId={links.checklist}
          />
          {brief.data === null && brief.error === null ? (
            <Center h={120}>
              <Loader size="sm" />
            </Center>
          ) : null}
        </Tabs.Panel>
        {setup && (
          <Tabs.Panel value="setup" pt="md">
            <SetupPanel reportId={reportId} header={header.data} onChanged={refresh} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="needs_you" pt="md">
          <NeedsYouPanel
            reportId={reportId}
            header={header.data}
            links={links}
            memory={needsYou}
            asks={open}
            asksLoaded={interventions.data !== null}
            onAnswered={refresh}
            onOpenSetup={openSetup}
          />
        </Tabs.Panel>
        <Tabs.Panel value="evidence" pt="md">
          <EvidencePanel
            reportId={reportId}
            header={header.data}
            links={links}
            onOpenFile={openFile}
          />
        </Tabs.Panel>
        <Tabs.Panel value="document" pt="md">
          <DocumentPanel
            reportId={reportId}
            header={header.data}
            links={links}
            onChanged={refresh}
            edits={edits}
            onEditsChange={setEdits}
            draftChanges={draftChanges}
            onDraftChangesChange={setDraftChanges}
            onAskCopilot={askCopilot}
            onClosedHere={closedHere}
          />
        </Tabs.Panel>
        <Tabs.Panel value="files" pt="md">
          <FilesPanel reportId={reportId} header={header.data} links={links} />
        </Tabs.Panel>
        <Tabs.Panel value="chat" pt="md">
          <ConversationsPanel
            reportId={reportId}
            header={header.data}
            links={links}
            onOpenSetup={openSetup}
          />
        </Tabs.Panel>
        <Tabs.Panel value="activity" pt="md">
          <ActivityPanel
            reportId={reportId}
            familyKey={familyKey}
            header={header.data}
            links={links}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );

  // The views keep their place in the tree whether or not the column is
  // beside them, so opening or closing it never remounts them.
  const besideViews = docked && column !== null;
  return (
    <RecordIndexProvider index={index.data} open={openRecord} openId={links.record}>
      <div className={besideViews ? classes.withColumn : undefined}>
        {workbench}
        {besideViews ? <aside className={classes.column}>{column}</aside> : null}
      </div>
      {!docked && (
        <Drawer
          opened={column !== null}
          onClose={links.record ? closeRecord : closeCopilot}
          position="right"
          size="lg"
          withCloseButton={false}
          lockScroll={false}
        >
          <Box h="calc(100dvh - 2 * var(--mantine-spacing-md))">{column}</Box>
        </Drawer>
      )}
    </RecordIndexProvider>
  );
}

// ---------------------------------------------------------------------------
// The views, each reading only while it's open (tabs don't keep panels
// mounted) and only when the case's version moves. Memoised: a panel
// re-renders for its own props, not the page's.
// ---------------------------------------------------------------------------

interface PanelProps {
  reportId: string;
  header: Header;
  links: WorkbenchLinks;
}

/** Setup: read on its own version too, since the request's triage and its
 * partner assignments move on their own clocks. */
const SetupPanel = memo(
  ({
    reportId,
    header,
    onChanged,
  }: Readonly<{ reportId: string; header: Header; onChanged: () => Promise<void> }>) => {
    const setup = useWorkbenchSetup(reportId, header.version);
    const { refetch } = setup;
    const changed = useCallback(async () => {
      await Promise.all([refetch(), onChanged()]);
    }, [refetch, onChanged]);
    if (!setup.data) {
      return setup.error ? (
        <Text size="sm" c="dimmed">
          The setup couldn&apos;t be loaded: {setup.error.message}
        </Text>
      ) : (
        <Center h={120}>
          <Loader size="sm" />
        </Center>
      );
    }
    return <SetupView setup={setup.data} header={header} onChanged={changed} />;
  }
);

const NeedsYouPanel = memo(
  ({
    reportId,
    header,
    links,
    memory,
    asks,
    asksLoaded,
    onAnswered,
    onOpenSetup,
  }: Readonly<
    PanelProps & {
      memory: NeedsYouMemory;
      asks: Intervention[];
      asksLoaded: boolean;
      onAnswered: () => Promise<void>;
      onOpenSetup?: () => void;
    }
  >) => {
    const issues = useWorkbenchReviewIssues(reportId, header.version);
    const documents = useWorkbenchDocuments(reportId, header.version);
    const checklist = useWorkbenchChecklist(reportId, header.version);
    const { focus } = links;
    // The item in focus, in the URL; a move within the list replaces it.
    const select = useCallback(
      (item: NeedsYouItem | null) =>
        focus(
          {
            view: 'needs_you',
            ...(item?.kind === 'issue' ? { issue: item.id } : { intervention: item?.id ?? null }),
          },
          { replace: true }
        ),
      [focus]
    );
    // A review issue opens in the draft's margin.
    const openIssue = useCallback(
      (issue: ReviewIssue) =>
        focus({ view: 'document', doc: header.needsYou.draftId, issue: issue.id }),
      [focus, header.needsYou.draftId]
    );
    // A partner's return (a rework is about one) opens in the Document view.
    const openDocument = useCallback((doc: string) => focus({ view: 'document', doc }), [focus]);
    const loaded =
      asksLoaded &&
      (issues.data !== null || issues.error !== null) &&
      (documents.data !== null || documents.error !== null) &&
      (checklist.data !== null || checklist.error !== null);
    return (
      <NeedsYou
        header={header}
        asks={asks}
        issues={issues.data}
        documents={documents.data}
        checklist={checklist.data}
        loaded={loaded}
        selectedId={links.issue ?? links.intervention}
        memory={memory}
        onSelect={select}
        recordOpen={links.record !== null}
        onOpenIssue={openIssue}
        onAnswered={onAnswered}
        onOpenSetup={onOpenSetup}
        onOpenDocument={openDocument}
      />
    );
  }
);

const EvidencePanel = memo(
  ({
    reportId,
    header,
    links,
    onOpenFile,
  }: Readonly<PanelProps & { onOpenFile: (key: string) => void }>) => {
    const index = useWorkbenchEvidenceIndex(reportId, header.version);
    const checklist = useWorkbenchChecklist(reportId, header.version);
    const files = useWorkbenchFiles(reportId, header.version);
    const focusedId = links.evidence;
    // The person's tab, subject and page hold until another record is opened
    // here; a record opened opens its own tab and page; a checklist item in
    // focus narrows the list to its records.
    const [chosen, setChosen] = useState<{
      focus: string | null;
      filters: EvidenceFilters;
    } | null>(null);
    const filters = useMemo(() => {
      if (!index.data) {
        return null;
      }
      if (chosen && chosen.focus === focusedId) {
        return chosen.filters;
      }
      return (focusedId ? placeOf(index.data, focusedId) : null) ?? firstFilters(index.data);
    }, [chosen, focusedId, index.data]);
    const changeFilters = useCallback(
      (next: EvidenceFilters) => setChosen({ focus: focusedId, filters: next }),
      [focusedId]
    );
    const query = useMemo(
      () =>
        filters
          ? { ...evidenceQuery(filters), checklistItemId: links.checklist ?? undefined }
          : null,
      [filters, links.checklist]
    );
    const evidence = useWorkbenchEvidence(reportId, header.version, query);
    // A record opened that isn't on the page read (a newer record replaced
    // it): read by its id.
    const offPage =
      focusedId !== null &&
      evidence.data !== null &&
      !evidence.data.items.some((item) => item.id === focusedId)
        ? focusedId
        : null;
    const opened = useWorkbenchEvidenceItem(reportId, offPage, header.version);
    const { focus } = links;
    const openChecklistItem = useCallback(
      (id: string) => focus({ view: 'brief', checklist: id }),
      [focus]
    );
    const showParagraph = useCallback((para: string) => focus({ view: 'document', para }), [focus]);
    const item = links.checklist
      ? (checklist.data?.find((c) => c.id === links.checklist) ?? null)
      : null;
    return (
      <Stack gap="sm">
        {links.checklist ? (
          <Text size="xs" c="dimmed" data-testid="evidence-narrowed">
            The records on {item ? `${item.key} ${item.title}` : 'one checklist item'} ·{' '}
            <Text
              span
              size="xs"
              c="blue"
              style={{ cursor: 'pointer' }}
              onClick={() => focus({ view: 'evidence' })}
            >
              every record
            </Text>
          </Text>
        ) : null}
        <EvidenceView
          header={header}
          index={index.data}
          evidence={filters ? evidence.data : null}
          filters={filters ?? { tab: 'all', subjectId: null, page: 1 }}
          onFiltersChange={changeFilters}
          checklist={checklist.data}
          files={files.data}
          focusedId={focusedId}
          opened={opened.data}
          openedMissing={opened.error !== null}
          onOpenChecklistItem={openChecklistItem}
          onShowParagraph={showParagraph}
          onOpenFile={onOpenFile}
        />
      </Stack>
    );
  }
);

interface DocumentPanelProps extends PanelProps {
  onAskCopilot: (passage: CopilotPassage) => void;
  onChanged: () => Promise<void>;
  edits: PendingEdits;
  onEditsChange: (edits: PendingEdits) => void;
  draftChanges: DraftChanges | null;
  onDraftChangesChange: (changes: DraftChanges | null) => void;
  onClosedHere: (issueIds: string[]) => void;
}

const DocumentPanel = memo(
  ({
    reportId,
    header,
    links,
    onChanged,
    edits,
    onEditsChange,
    draftChanges,
    onDraftChangesChange,
    onAskCopilot,
    onClosedHere,
  }: Readonly<DocumentPanelProps>) => {
    const evidence = useWorkbenchEvidence(reportId, header.version, DOCUMENT_EVIDENCE);
    const checklist = useWorkbenchChecklist(reportId, header.version);
    const documents = useWorkbenchDocuments(reportId, header.version);
    const prefills = useWorkbenchPrefills(reportId, header.version);
    const reviewIssues = useWorkbenchReviewIssues(reportId, header.version);
    return (
      <DocumentView
        documents={documents.data}
        prefills={prefills.data}
        reviewIssues={reviewIssues.data}
        header={header}
        evidence={evidence.data}
        checklist={checklist.data}
        focusedPara={links.para}
        focusedDoc={links.doc}
        focusedIssue={links.issue}
        onFocusIssue={(issue) =>
          links.focus({ view: 'document', doc: links.doc, issue }, { replace: true })
        }
        needsYouTotal={header.needsYou.total}
        onBackToNeedsYou={(issue) => links.focus({ view: 'needs_you', issue })}
        onClosedHere={onClosedHere}
        onAskCopilot={onAskCopilot}
        onOpenEvidence={links.openRecord}
        onOpenChecklistItem={(id) => links.focus({ view: 'brief', checklist: id })}
        onChanged={onChanged}
        edits={edits}
        onEditsChange={onEditsChange}
        draftChanges={draftChanges}
        onDraftChangesChange={onDraftChangesChange}
      />
    );
  }
);

const FilesPanel = memo(({ reportId, header, links }: Readonly<PanelProps>) => {
  const files = useWorkbenchFiles(reportId, header.version);
  return (
    <FilesView
      reportId={reportId}
      files={files.data}
      error={files.error}
      focusedKey={links.file}
      onUploaded={files.refetch}
    />
  );
});

/** The case's conversations: the partner in focus is in the URL, so a
 * link opens one; moving between them replaces, so Back leaves the view. */
const ConversationsPanel = memo(
  ({
    reportId,
    header,
    links,
    onOpenSetup,
  }: Readonly<PanelProps & { onOpenSetup?: () => void }>) => {
    const conversations = useWorkbenchConversations(reportId, header.version);
    const { focus } = links;
    const onFocusPartner = useCallback(
      (id: string | null) => focus({ view: 'chat', partner: id }, { replace: true }),
      [focus]
    );
    return (
      <ConversationsView
        reportId={reportId}
        header={header}
        conversations={conversations.data}
        error={conversations.error}
        partnerId={links.partner}
        onFocusPartner={onFocusPartner}
        onOpenSetup={onOpenSetup}
      />
    );
  }
);

const ActivityPanel = memo(
  ({ reportId, familyKey, header, links }: Readonly<PanelProps & { familyKey: string }>) => {
    const activity = useWorkbenchActivity(reportId, header.version);
    return (
      <ActivityView
        reportId={reportId}
        familyKey={familyKey}
        activity={activity.data}
        error={activity.error}
        onOpen={(t) => {
          switch (t.type) {
            case 'evidence':
              links.openRecord(t.id);
              break;
            case 'checklist':
              links.focus({ view: 'brief', checklist: t.id });
              break;
            case 'intervention':
              links.focus({ view: 'needs_you', intervention: t.id });
              break;
            default:
              break;
          }
        }}
      />
    );
  }
);
